import { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { showToast } from "../toastService";
import { apiFetch } from "../utils/api";
import Header from "../components/Header";
import BottomNavbar from "../components/BottomNavbar";
import "./css/AddPastWork.css";

import noWorkImage from "../assets/images/no_work.png";

const USER_BASE_URL = process.env.REACT_APP_USER_BASE_URL;
const VERSION = process.env.REACT_APP_VERSION;

const MAX_PHOTOS = 5;
const MAX_DOCUMENTS = 5;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

// Allowed image types for photos
const ALLOWED_IMAGE_TYPES = [
  "image/png",
  "image/jpg",
  "image/jpeg",
  "image/svg+xml",
  "image/webp",
  "image/avif",
  "image/heic",
  "image/heif",
];

// Allowed types for documents (images + PDF + DOC)
const ALLOWED_DOC_TYPES = [
  "image/png",
  "image/jpg",
  "image/jpeg",
  "image/svg+xml",
  "image/webp",
  "image/avif",
  "image/heic",
  "image/heif",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

export default function EditPastWork() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [lang] = useState(localStorage.getItem("app_language") || "en");
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [workNotFound, setWorkNotFound] = useState(false);
  
  // Form fields
  const [title, setTitle] = useState("");
  const [budget, setBudget] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [completionDate, setCompletionDate] = useState("");
  
  // File uploads
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [newPhotos, setNewPhotos] = useState([]);
  const [newDocuments, setNewDocuments] = useState([]);
  
  // Deleting states
  const [deletingPhotoId, setDeletingPhotoId] = useState(null);
  const [deletingDocumentId, setDeletingDocumentId] = useState(null);
  
  // Refs
  const photoInputRef = useRef(null);
  const documentInputRef = useRef(null);
  
  // Validation errors
  const [errors, setErrors] = useState({});

  // Translations
  const t = {
    en: {
      pastWorks: "Past Works",
      editPastWork: "Edit Past Work",
      fillForm: "Fill the below form to edit past work",
      title: "Title",
      titlePlaceholder: "Title of your project",
      photos: "Photos",
      photosInfo: "Max 5 photos allowed, upload png or jpg only",
      clickToUpload: "Click Here To Upload",
      budget: "Budget",
      workStartDate: "Work Start Date",
      workCompletionDate: "Work Completion Date",
      clickToSelect: "Click Here to Select",
      description: "Description",
      descriptionPlaceholder: "Add the work description here...",
      workDocuments: "Work Documents",
      documentsInfo: "Max 5 documents allowed, upload pdf or jpg only",
      saveWork: "Save Work",
      loading: "Loading",
      invalidFileType: "Invalid file type",
      fileTooLarge: "File size must be less than 5MB",
      maxFilesReached: "Maximum files reached",
      atLeastOnePhoto: "At least one photo is required",
      titleRequired: "Title is required",
      titleMaxLength: "Title must be less than 100 characters",
      budgetRequired: "Budget is required",
      budgetInvalid: "Budget must be a valid number between 0 and 1,000,000,000",
      descriptionRequired: "Description is required",
      descriptionMaxLength: "Description must be less than 2000 characters",
      startDateRequired: "Work start date is required",
      completionDateRequired: "Work completion date is required",
      completionBeforeStart: "Completion date must be after start date",
      editSuccess: "Past work updated successfully!",
      editFailed: "Failed to update past work",
      noWorkFound: "No work found for this ID",
      failedToLoad: "Failed to load work details",
      deleteFailed: "Failed to delete",
    },
    ar: {
      pastWorks: "الأعمال السابقة",
      editPastWork: "تعديل عمل سابق",
      fillForm: "املأ النموذج أدناه لتعديل العمل السابق",
      title: "العنوان",
      titlePlaceholder: "عنوان مشروعك",
      photos: "الصور",
      photosInfo: "الحد الأقصى 5 صور، قم بتحميل PNG أو JPG فقط",
      clickToUpload: "انقر هنا للتحميل",
      budget: "الميزانية",
      workStartDate: "تاريخ بدء العمل",
      workCompletionDate: "تاريخ إتمام العمل",
      clickToSelect: "انقر هنا للاختيار",
      description: "الوصف",
      descriptionPlaceholder: "أضف وصف العمل هنا...",
      workDocuments: "مستندات العمل",
      documentsInfo: "الحد الأقصى 5 مستندات، قم بتحميل PDF أو JPG فقط",
      saveWork: "حفظ العمل",
      loading: "جاري التحميل",
      invalidFileType: "نوع ملف غير صحيح",
      fileTooLarge: "يجب أن يكون حجم الملف أقل من 5 ميجابايت",
      maxFilesReached: "تم الوصول إلى الحد الأقصى للملفات",
      atLeastOnePhoto: "مطلوب صورة واحدة على الأقل",
      titleRequired: "العنوان مطلوب",
      titleMaxLength: "يجب أن يكون العنوان أقل من 100 حرف",
      budgetRequired: "الميزانية مطلوبة",
      budgetInvalid: "يجب أن تكون الميزانية رقماً صحيحاً بين 0 و 1,000,000,000",
      descriptionRequired: "الوصف مطلوب",
      descriptionMaxLength: "يجب أن يكون الوصف أقل من 2000 حرف",
      startDateRequired: "تاريخ بدء العمل مطلوب",
      completionDateRequired: "تاريخ إتمام العمل مطلوب",
      completionBeforeStart: "يجب أن يكون تاريخ الإتمام بعد تاريخ البدء",
      editSuccess: "تم تحديث العمل السابق بنجاح!",
      editFailed: "فشل تحديث العمل السابق",
      noWorkFound: "لم يتم العثور على عمل لهذا المعرف",
      failedToLoad: "فشل تحميل تفاصيل العمل",
      deleteFailed: "فشل الحذف",
    },
  }[lang];

  // Fetch work details on mount
  useEffect(() => {
    const fetchWorkDetails = async () => {
      try {
        setInitialLoading(true);
        const data = await apiFetch(
          `${USER_BASE_URL}/${VERSION}/past-works/`,
          "POST",
          JSON.stringify({ id }),
          { "Content-Type": "application/json" }
        );

        if (data && data.response_code === 200 && data.response) {
          const work = data.response;
          
          // Populate form fields
          setTitle(work.title || "");
          setBudget(work.budget?.toString() || "");
          setDescription(work.description || "");
          
          // Format dates
          if (work.start_date) {
            const startDateObj = new Date(work.start_date);
            if (!isNaN(startDateObj.getTime())) {
              const startYear = startDateObj.getFullYear();
              const startMonth = String(startDateObj.getMonth() + 1).padStart(2, '0');
              const startDay = String(startDateObj.getDate()).padStart(2, '0');
              setStartDate(`${startYear}-${startMonth}-${startDay}`);
            }
          }
          
          // Check for completion_date (handle both completion_date and completionDate)
          const completionDateValue = work.completion_date || work.completionDate;
          if (completionDateValue) {
            const completionDateObj = new Date(completionDateValue);
            if (!isNaN(completionDateObj.getTime())) {
              const completionYear = completionDateObj.getFullYear();
              const completionMonth = String(completionDateObj.getMonth() + 1).padStart(2, '0');
              const completionDay = String(completionDateObj.getDate()).padStart(2, '0');
              setCompletionDate(`${completionYear}-${completionMonth}-${completionDay}`);
            }
          }
          
          // Set existing photos and documents
          setExistingPhotos(work.photos || []);
          setExistingDocuments(work.documents || []);
        } else {
          setWorkNotFound(true);
        }
      } catch (err) {
        console.error("Error fetching work details:", err);
        setWorkNotFound(true);
        showToast(err.message || t.failedToLoad, "error");
      } finally {
        setInitialLoading(false);
      }
    };

    if (id) {
      fetchWorkDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Validate file type for photos
  const validatePhotoFile = (file) => {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      const fileName = file.name.toLowerCase();
      const hasValidExtension = [".png", ".jpg", ".jpeg", ".svg", ".webp", ".avif", ".heic", ".heif"].some(
        (ext) => fileName.endsWith(ext)
      );
      if (!hasValidExtension) {
        return { valid: false, error: t.invalidFileType };
      }
    }
    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: t.fileTooLarge };
    }
    return { valid: true };
  };

  // Validate file type for documents
  const validateDocumentFile = (file) => {
    if (!ALLOWED_DOC_TYPES.includes(file.type)) {
      const fileName = file.name.toLowerCase();
      const hasValidExtension = [".png", ".jpg", ".jpeg", ".svg", ".webp", ".avif", ".heic", ".pdf", ".doc", ".docx"].some(
        (ext) => fileName.endsWith(ext)
      );
      if (!hasValidExtension) {
        return { valid: false, error: t.invalidFileType };
      }
    }
    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: t.fileTooLarge };
    }
    return { valid: true };
  };

  // Handle photo selection
  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    const totalPhotos = existingPhotos.length + newPhotos.length;
    if (totalPhotos + files.length > MAX_PHOTOS) {
      showToast(t.maxFilesReached, "error");
      return;
    }

    const validFiles = [];
    for (const file of files) {
      const validation = validatePhotoFile(file);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        showToast(validation.error, "error");
      }
    }

    if (validFiles.length > 0) {
      const newTotal = totalPhotos + validFiles.length;
      if (newTotal > MAX_PHOTOS) {
        const remaining = MAX_PHOTOS - totalPhotos;
        setNewPhotos([...newPhotos, ...validFiles.slice(0, remaining)]);
        showToast(`Only ${remaining} more photo(s) can be uploaded.`, "error");
      } else {
        setNewPhotos([...newPhotos, ...validFiles]);
      }
    }

    if (photoInputRef.current) {
      photoInputRef.current.value = "";
    }
  };

  // Handle document selection
  const handleDocumentSelect = (e) => {
    const files = Array.from(e.target.files);
    const totalDocuments = existingDocuments.length + newDocuments.length;
    if (totalDocuments + files.length > MAX_DOCUMENTS) {
      showToast(t.maxFilesReached, "error");
      return;
    }

    const validFiles = [];
    for (const file of files) {
      const validation = validateDocumentFile(file);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        showToast(validation.error, "error");
      }
    }

    if (validFiles.length > 0) {
      const newTotal = totalDocuments + validFiles.length;
      if (newTotal > MAX_DOCUMENTS) {
        const remaining = MAX_DOCUMENTS - totalDocuments;
        setNewDocuments([...newDocuments, ...validFiles.slice(0, remaining)]);
        showToast(`Only ${remaining} more document(s) can be uploaded.`, "error");
      } else {
        setNewDocuments([...newDocuments, ...validFiles]);
      }
    }

    if (documentInputRef.current) {
      documentInputRef.current.value = "";
    }
  };

  // Remove new photo
  const removeNewPhoto = (index) => {
    setNewPhotos(newPhotos.filter((_, i) => i !== index));
  };

  // Remove new document
  const removeNewDocument = (index) => {
    setNewDocuments(newDocuments.filter((_, i) => i !== index));
  };

  // Delete existing photo
  const handleDeleteExistingPhoto = async (photoId) => {
    if (deletingPhotoId || deletingDocumentId) return;
    
    try {
      setDeletingPhotoId(photoId);
      const data = await apiFetch(
        `${USER_BASE_URL}/${VERSION}/past-works/element-delete`,
        "POST",
        JSON.stringify({
          key: photoId,
          target_field: "photos",
          id: id,
        }),
        { "Content-Type": "application/json" }
      );

      if (data && data.response_code === 200) {
        setExistingPhotos(existingPhotos.filter((photo) => photo.id !== photoId));
        showToast(lang === "ar" ? "تم الحذف بنجاح" : "Deleted successfully", "success");
      } else {
        showToast(data?.message || t.deleteFailed, "error");
      }
    } catch (err) {
      console.error("Error deleting photo:", err);
      showToast(err.message || t.deleteFailed, "error");
    } finally {
      setDeletingPhotoId(null);
    }
  };

  // Delete existing document
  const handleDeleteExistingDocument = async (documentId) => {
    if (deletingPhotoId || deletingDocumentId) return;
    
    try {
      setDeletingDocumentId(documentId);
      const data = await apiFetch(
        `${USER_BASE_URL}/${VERSION}/past-works/element-delete`,
        "POST",
        JSON.stringify({
          key: documentId,
          target_field: "documents",
          id: id,
        }),
        { "Content-Type": "application/json" }
      );

      if (data && data.response_code === 200) {
        setExistingDocuments(existingDocuments.filter((doc) => doc.id !== documentId));
        showToast(lang === "ar" ? "تم الحذف بنجاح" : "Deleted successfully", "success");
      } else {
        showToast(data?.message || t.deleteFailed, "error");
      }
    } catch (err) {
      console.error("Error deleting document:", err);
      showToast(err.message || t.deleteFailed, "error");
    } finally {
      setDeletingDocumentId(null);
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    const totalPhotos = existingPhotos.length + newPhotos.length;

    // Title validation
    if (!title.trim() || title.trim().length === 0) {
      newErrors.title = t.titleRequired;
    } else if (title.trim().length > 100) {
      newErrors.title = t.titleMaxLength;
    }

    // Budget validation
    const budgetStr = budget.toString().trim();
    if (!budgetStr || budgetStr === "" || budgetStr === ".") {
      newErrors.budget = t.budgetRequired;
    } else {
      const budgetNum = parseFloat(budgetStr);
      if (isNaN(budgetNum) || budgetNum < 0 || budgetNum > 1000000000) {
        newErrors.budget = t.budgetInvalid;
      }
    }

    // Description validation
    if (!description.trim() || description.trim().length === 0) {
      newErrors.description = t.descriptionRequired;
    } else if (description.trim().length > 2000) {
      newErrors.description = t.descriptionMaxLength;
    }

    // Start date validation
    if (!startDate) {
      newErrors.startDate = t.startDateRequired;
    }

    // Completion date validation
    if (!completionDate) {
      newErrors.completionDate = t.completionDateRequired;
    } else if (startDate && completionDate) {
      const start = new Date(startDate);
      const completion = new Date(completionDate);
      if (completion <= start) {
        newErrors.completionDate = t.completionBeforeStart;
      }
    }

    // At least one photo required
    if (totalPhotos === 0) {
      newErrors.photos = t.atLeastOnePhoto;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle budget change
  const handleBudgetChange = (e) => {
    const value = e.target.value;
    // Allow empty string, numbers, and decimals
    if (value === "" || value === "." || /^\d*\.?\d*$/.test(value)) {
      setBudget(value);
      if (errors.budget) setErrors({ ...errors, budget: null });
    }
  };

  // Get today's date in YYYY-MM-DD format for max date
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!validateForm()) {
      // Prevent scrolling to error field
      const firstErrorField = document.querySelector('.form-input.error, .form-textarea.error');
      if (firstErrorField) {
        firstErrorField.focus({ preventScroll: true });
      }
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();

      // Add form fields
      formData.append("title", title.trim());
      formData.append("budget", parseFloat(budget));
      formData.append("description", description.trim());
      formData.append("start_date", startDate);
      formData.append("completion_date", completionDate);

      // Add new photos
      newPhotos.forEach((photo) => {
        formData.append("photos", photo);
      });

      // Add new documents
      newDocuments.forEach((document) => {
        formData.append("documents", document);
      });

      const accessToken = localStorage.getItem("accessToken");
      const response = await fetch(`${USER_BASE_URL}/${VERSION}/past-works/edit/${id}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "app-version": "1.0.0",
        },
        body: formData,
      });

      const data = await response.json();

      if (data && data.response_code === 200) {
        showToast(t.editSuccess, "success");
        navigate("/past-works");
      } else {
        showToast(data.message || t.editFailed, "error");
      }
    } catch (err) {
      console.error("Error updating past work:", err);
      showToast(err.message || t.editFailed, "error");
    } finally {
      setLoading(false);
    }
  };

  // Get preview URL for photo
  const getPhotoPreview = (fileOrUrl) => {
    if (fileOrUrl instanceof File) {
      return URL.createObjectURL(fileOrUrl);
    }
    return fileOrUrl.url || fileOrUrl;
  };

  // Get file extension for document icon
  const getDocumentExtension = (fileOrDoc) => {
    let fileName = "";
    if (fileOrDoc instanceof File) {
      fileName = fileOrDoc.name.toLowerCase();
    } else if (fileOrDoc.url) {
      const urlParts = fileOrDoc.url.split("/");
      fileName = urlParts[urlParts.length - 1].toLowerCase();
    }
    
    if (fileName.endsWith(".pdf")) return "PDF";
    if (fileName.endsWith(".doc") || fileName.endsWith(".docx")) return "DOC";
    if (fileName.endsWith(".jpg") || fileName.endsWith(".jpeg")) return "JPG";
    if (fileName.endsWith(".png")) return "PNG";
    return "FILE";
  };

  // Loading screen
  if (initialLoading) {
    return (
      <div className="add-past-work-container" dir={lang === "ar" ? "rtl" : "ltr"}>
        <Header lang={lang} />
        <div className="edit-past-work-loading">
          <div className="spinner"></div>
          <p>{t.loading}</p>
        </div>
        <BottomNavbar currentPage="settings" lang={lang} />
      </div>
    );
  }

  // Work not found
  if (workNotFound) {
    return (
      <div className="add-past-work-container" dir={lang === "ar" ? "rtl" : "ltr"}>
        <Header lang={lang} />
        <div className="add-past-work-page-header">
          <button className="back-button" onClick={() => navigate(-1)}>
            <i className="bx bx-arrow-back"></i>
          </button>
          <h1 className="add-past-work-page-title">{t.pastWorks}</h1>
          <div className="header-spacer"></div>
        </div>
        <div className="add-past-work-content" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "50vh", gap: "16px", textAlign: "center" }}>
          <img src={noWorkImage} alt="No work" style={{ maxWidth: "300px", width: "100%" }} />
          <p style={{ fontSize: "16px", color: "#6b7280", margin: 0 }}>{t.noWorkFound}</p>
        </div>
        <BottomNavbar currentPage="settings" lang={lang} />
      </div>
    );
  }

  const totalPhotos = existingPhotos.length + newPhotos.length;
  const totalDocuments = existingDocuments.length + newDocuments.length;
  const canUploadMorePhotos = totalPhotos < MAX_PHOTOS;
  const canUploadMoreDocuments = totalDocuments < MAX_DOCUMENTS;

  return (
    <div className="add-past-work-container" dir={lang === "ar" ? "rtl" : "ltr"}>
      {/* Header */}
      <Header lang={lang} />

      {/* Page Header */}
      <div className="add-past-work-page-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <i className="bx bx-arrow-back"></i>
        </button>
        <h1 className="add-past-work-page-title">{t.pastWorks}</h1>
        <div className="header-spacer"></div>
      </div>

      {/* Main Content */}
      <div className="add-past-work-content">
        <h2 className="add-past-work-heading">{t.editPastWork}</h2>
        <p className="add-past-work-subtitle">{t.fillForm}</p>

        <form onSubmit={handleSubmit}>
          {/* Title */}
          <div className="form-field">
            <label className="form-label">{t.title}</label>
            <input
              type="text"
              className={`form-input ${errors.title ? "error" : ""}`}
              placeholder={t.titlePlaceholder}
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (errors.title) setErrors({ ...errors, title: null });
              }}
              maxLength={100}
            />
            {errors.title && <span className="error-message">{errors.title}</span>}
          </div>

          {/* Photos */}
          <div className="form-field">
            <label className="form-label">{t.photos}</label>
            <p className="form-info">{t.photosInfo}</p>
            <div className="file-upload-section">
              <input
                ref={photoInputRef}
                type="file"
                multiple
                accept=".png,.jpg,.jpeg,.svg,.webp,.avif,.heic,.heif"
                onChange={handlePhotoSelect}
                className="file-input-hidden"
                disabled={!canUploadMorePhotos}
              />
              
              {/* Existing Photos */}
              {existingPhotos.length > 0 && (
                <div className="photo-grid">
                  {existingPhotos.map((photo, index) => (
                    <div key={photo.id || index} className="photo-item">
                      <img
                        src={photo.url}
                        alt={`${index + 1}`}
                        className="photo-preview"
                      />
                      <button
                        type="button"
                        onClick={() => handleDeleteExistingPhoto(photo.id)}
                        className="photo-delete-btn"
                        aria-label="Delete photo"
                        disabled={deletingPhotoId === photo.id || deletingDocumentId !== null}
                      >
                        {deletingPhotoId === photo.id ? (
                          <div className="delete-spinner"></div>
                        ) : (
                          <i className="bx bx-trash"></i>
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* New Photo Previews */}
              {newPhotos.length > 0 && (
                <div className="photo-grid">
                  {newPhotos.map((photo, index) => (
                    <div key={index} className="photo-item">
                      <img
                        src={getPhotoPreview(photo)}
                        alt={`${existingPhotos.length + index + 1}`}
                        className="photo-preview"
                      />
                      <button
                        type="button"
                        onClick={() => removeNewPhoto(index)}
                        className="photo-delete-btn"
                        aria-label="Delete photo"
                      >
                        <i className="bx bx-trash"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload Area */}
              {canUploadMorePhotos && (
                <div
                  onClick={() => photoInputRef.current?.click()}
                  className="file-upload-area"
                >
                  <p>{t.clickToUpload}</p>
                </div>
              )}
            </div>
            {errors.photos && <span className="error-message">{errors.photos}</span>}
          </div>

          {/* Budget */}
          <div className="form-field">
            <label className="form-label">{t.budget}</label>
            <input
              type="number"
              className={`form-input ${errors.budget ? "error" : ""}`}
              placeholder="0"
              value={budget}
              onChange={handleBudgetChange}
              min="0"
              max="1000000000"
              step="0.01"
            />
            {errors.budget && <span className="error-message">{errors.budget}</span>}
          </div>

          {/* Work Start Date */}
          <div className="form-field">
            <label className="form-label">{t.workStartDate}</label>
            <div className="date-input-wrapper">
              <input
                type="date"
                className={`form-input date-input ${errors.startDate ? "error" : ""}`}
                placeholder={t.clickToSelect}
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  if (errors.startDate) setErrors({ ...errors, startDate: null });
                }}
                max={completionDate ? (completionDate < getTodayDate() ? completionDate : getTodayDate()) : getTodayDate()}
              />
              <i className="bx bx-calendar date-icon"></i>
            </div>
            {errors.startDate && <span className="error-message">{errors.startDate}</span>}
          </div>

          {/* Work Completion Date */}
          <div className="form-field">
            <label className="form-label">{t.workCompletionDate}</label>
            <div className="date-input-wrapper">
              <input
                type="date"
                className={`form-input date-input ${errors.completionDate ? "error" : ""}`}
                placeholder={t.clickToSelect}
                value={completionDate}
                onChange={(e) => {
                  setCompletionDate(e.target.value);
                  if (errors.completionDate) setErrors({ ...errors, completionDate: null });
                }}
                min={startDate || undefined}
                max={getTodayDate()}
              />
              <i className="bx bx-calendar date-icon"></i>
            </div>
            {errors.completionDate && <span className="error-message">{errors.completionDate}</span>}
          </div>

          {/* Description */}
          <div className="form-field">
            <label className="form-label">{t.description}</label>
            <textarea
              className={`form-textarea ${errors.description ? "error" : ""}`}
              placeholder={t.descriptionPlaceholder}
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                if (errors.description) setErrors({ ...errors, description: null });
              }}
              maxLength={2000}
              rows={4}
            />
            {errors.description && <span className="error-message">{errors.description}</span>}
          </div>

          {/* Work Documents */}
          <div className="form-field">
            <label className="form-label">{t.workDocuments}</label>
            <p className="form-info">{t.documentsInfo}</p>
            <div className="file-upload-section">
              <input
                ref={documentInputRef}
                type="file"
                multiple
                accept=".png,.jpg,.jpeg,.svg,.webp,.avif,.heic,.pdf,.doc,.docx"
                onChange={handleDocumentSelect}
                className="file-input-hidden"
                disabled={!canUploadMoreDocuments}
              />
              
              {/* Existing Documents */}
              {existingDocuments.length > 0 && (
                <div className="document-grid">
                  {existingDocuments.map((document, index) => (
                    <div key={document.id || index} className="document-item">
                      <div className="document-icon">
                        {getDocumentExtension(document)}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDeleteExistingDocument(document.id)}
                        className="document-delete-btn"
                        aria-label="Delete document"
                        disabled={deletingDocumentId === document.id || deletingPhotoId !== null}
                      >
                        {deletingDocumentId === document.id ? (
                          <div className="delete-spinner"></div>
                        ) : (
                          <i className="bx bx-x"></i>
                        )}
                      </button>
                      <p className="document-name">
                        {document.url ? (document.url.split("/").pop().length > 15
                          ? document.url.split("/").pop().substring(0, 12) + "..."
                          : document.url.split("/").pop()) : "Document"}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {/* New Document Previews */}
              {newDocuments.length > 0 && (
                <div className="document-grid">
                  {newDocuments.map((document, index) => (
                    <div key={index} className="document-item">
                      <div className="document-icon">
                        {getDocumentExtension(document)}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeNewDocument(index)}
                        className="document-delete-btn"
                        aria-label="Delete document"
                      >
                        <i className="bx bx-x"></i>
                      </button>
                      <p className="document-name">
                        {document.name.length > 15
                          ? document.name.substring(0, 12) + "..."
                          : document.name}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {/* Upload Area */}
              {canUploadMoreDocuments && (
                <div
                  onClick={() => documentInputRef.current?.click()}
                  className="file-upload-area"
                >
                  <p>{t.clickToUpload}</p>
                </div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="add-work-btn"
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="button-spinner"></div>
                {t.loading}
              </>
            ) : (
              t.saveWork
            )}
          </button>
        </form>
      </div>

      {/* Bottom Navigation */}
      <BottomNavbar currentPage="settings" lang={lang} />
    </div>
  );
}

